import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Menu, X, Phone } from "lucide-react";
import { CLINIC, MEDIA, NAV_LINKS, buildWaUrl } from "@/lib/data";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/60 shadow-[0_4px_24px_rgba(15,23,42,0.04)]"
          : "bg-white/40 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-[72px] lg:h-[80px] flex items-center justify-between gap-6">
        <a
          href="#acasa"
          className="flex items-center gap-3 group shrink-0"
          data-testid="header-logo-link"
        >
          <img
            src={MEDIA.logo}
            alt="Marimar Clinic logo"
            className="h-11 lg:h-12 w-auto transition-transform group-hover:scale-105"
            loading="eager"
          />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-display text-[17px] font-medium text-slate-900">
              Marimar Clinic
            </span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-brand-blue font-semibold">
              Sector 2 · București
            </span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Navigare principală">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="px-4 py-2 text-[14px] font-medium text-slate-700 hover:text-brand-blue rounded-full hover:bg-brand-soft transition-colors"
              data-testid={`nav-link-${link.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={buildWaUrl(CLINIC.dental.waNumber, "Bună ziua! Doresc o programare la Marimar Dental Clinic.")}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex items-center gap-2 bg-brand-blue text-white font-medium text-[14px] px-5 py-2.5 rounded-full hover:bg-brand-blueDark transition-all shadow-lg shadow-brand-blue/20 hover:shadow-xl hover:shadow-brand-blue/30 hover:-translate-y-0.5"
            data-testid="header-cta-programare"
          >
            <Phone className="w-4 h-4" />
            Programează-te
          </a>
          <button
            className="lg:hidden p-2.5 rounded-full hover:bg-brand-soft transition-colors"
            onClick={() => setOpen(true)}
            aria-label="Deschide meniu"
            data-testid="header-menu-open"
          >
            <Menu className="w-6 h-6 text-slate-900" />
          </button>
        </div>
      </div>

      {/* Mobile menu - rendered via portal to escape header's backdrop-filter containing block */}
      {createPortal(
        <div
          className={`fixed inset-0 z-[100] lg:hidden ${open ? "pointer-events-auto" : "pointer-events-none"}`}
          data-testid="mobile-menu"
          aria-hidden={!open}
        >
          <div
            className={`absolute inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`}
            onClick={() => setOpen(false)}
          />
          <div
            className={`absolute right-0 top-0 bottom-0 w-[88%] max-w-sm shadow-2xl p-6 flex flex-col transition-transform duration-500 ease-out ${
              open ? "translate-x-0" : "translate-x-full"
            }`}
            style={{ backgroundColor: "#ffffff" }}
          >
            <div className="flex items-center justify-between mb-8">
              <img src={MEDIA.logo} alt="Marimar Clinic" className="h-10" />
              <button
                onClick={() => setOpen(false)}
                className="p-2 rounded-full bg-slate-100 hover:bg-brand-soft"
                aria-label="Închide meniu"
                data-testid="mobile-menu-close"
              >
                <X className="w-6 h-6 text-slate-900" />
              </button>
            </div>
            <nav className="flex flex-col gap-2.5">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between px-5 py-4 text-[16px] font-medium text-slate-900 bg-white border border-slate-200 hover:border-brand-blue hover:bg-brand-soft hover:text-brand-blue rounded-2xl transition-all shadow-sm"
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                >
                  <span>{link.label}</span>
                  <span className="text-slate-300 text-[20px]">›</span>
                </a>
              ))}
            </nav>
            <div className="mt-auto pt-6 border-t border-slate-100 flex flex-col gap-3">
              <a
                href={buildWaUrl(CLINIC.dental.waNumber, "Bună ziua! Doresc o programare la Marimar Dental Clinic.")}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full text-center bg-brand-blue text-white font-medium px-5 py-3.5 rounded-full shadow-lg shadow-brand-blue/20"
                data-testid="mobile-cta-dental"
              >
                Programează la Dental
              </a>
              <a
                href={buildWaUrl(CLINIC.fizio.waNumber, "Bună ziua! Doresc o programare la Marimar Fizio & Kinetoterapie.")}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full text-center bg-brand-aqua text-white font-medium px-5 py-3.5 rounded-full shadow-lg shadow-brand-aqua/20"
                data-testid="mobile-cta-fizio"
              >
                Programează la Fizio
              </a>
            </div>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
}
